import { useMemo, useCallback } from "react";
import type { FileValidationProps, FileValidationViewModel, GroupedWarnings } from "./FileValidation.types";
import type { FileValidationWarning } from "../../../../GeoData/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers";

/**
 * Groups warnings by their issue type
 */
export function groupWarningsByType(warnings: FileValidationWarning[]): GroupedWarnings {
	return {
		unsupported_extension: warnings.filter((w) => w.issueType === "unsupported_extension"),
		parse_failed: warnings.filter((w) => w.issueType === "parse_failed"),
		empty_file: warnings.filter((w) => w.issueType === "empty_file"),
		invalid_geometry: warnings.filter((w) => w.issueType === "invalid_geometry"),
	};
}

/**
 * Hook for FileValidation step logic
 */
export function useFileValidationViewModel(props: FileValidationProps): FileValidationViewModel {
	const { warnings, uploadedFiles, onPrevious, onNext } = props;

	// Computed: whether there are any warnings
	const hasWarnings = useMemo(() => warnings.length > 0, [warnings]);

	// Computed: warnings grouped by type
	const warningsByType = useMemo(() => groupWarningsByType(warnings), [warnings]);

	// Computed: total number of warnings
	const totalWarnings = useMemo(() => warnings.length, [warnings]);

	// Computed: total number of files
	const totalFiles = useMemo(() => uploadedFiles.length, [uploadedFiles]);

	// Computed: whether there are any files uploaded
	const hasFiles = useMemo(() => uploadedFiles.length > 0, [uploadedFiles]);

	// Handler: Previous button
	const handlePrevious = useCallback(() => {
		onPrevious();
	}, [onPrevious]);

	// Handler: Next button
	const handleNext = useCallback(() => {
		onNext();
	}, [onNext]);

	return {
		hasWarnings,
		hasFiles,
		warningsByType,
		totalWarnings,
		totalFiles,
		handlePrevious,
		handleNext,
	};
}
